import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AggregationType, ColumnTypeEnum } from '@y42/json-schema';
import {
  ColumnVO,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { firstValueFrom } from 'rxjs';
import { DatasourceColumnUIModel } from '../../../state';

interface IServerSideGetRowsRequestWithFilter extends IServerSideGetRowsRequest {
  filter?: IServerSideGetRowsRequest['filterModel'];
}

@Injectable()
export class AggridDatasourceBuilder {
  constructor(private readonly http: HttpClient) {}

  build<T>(
    serverUrl: string,
    columns: DatasourceColumnUIModel[],
    responseCallBack: (response: T, getRowsParams: IServerSideGetRowsParams) => void,
    opts?: {
      requestBuilder?: (requestParams: IServerSideGetRowsParams) => unknown;
      responseErrorCallback?: (error: unknown, getRowsParams: IServerSideGetRowsParams) => void;
      headers?: HttpHeaders;
    },
  ): IServerSideDatasource {
    const processRequest = (request: IServerSideGetRowsRequestWithFilter) => {
      request.rowGroupCols.forEach((key: ColumnVO, index: number) => {
        const col = columns.find((c) => c.field_name === key.field);
        if (
          col &&
          (col.data_type === ColumnTypeEnum.Datetime ||
            col.data_type === ColumnTypeEnum.Timestamp ||
            col.data_type === ColumnTypeEnum.Time)
        ) {
          key.aggFunc = AggregationType.DateMinute;
          this.formatDateString(request, index);
        } else if (col && col.data_type === ColumnTypeEnum.Date) {
          key.aggFunc = AggregationType.DayMonthYear;
          this.formatDateString(request, index);
        } else {
          key.aggFunc = AggregationType.Value;
        }
        if (
          request.groupKeys[index] !== undefined &&
          ((request.groupKeys[index] as unknown) === false || (request.groupKeys[index] as unknown) === true) // TODO: do we really pass booleans somehow?
        ) {
          request.groupKeys[index] = `${request.groupKeys[index]}`;
        }
      });
      request.valueCols = request.valueCols.filter((item) => {
        return request.rowGroupCols.filter((f) => f.field === item.field).length === 0;
      });

      const filter = request.filterModel;
      delete request.filterModel;
      if (filter) {
        request.filter = Object.keys(filter).map((item) => ({
          variable_field_name: item,
          ...filter[item],
        }));
      }
    };

    return {
      getRows: (requestParams: IServerSideGetRowsParams) => {
        processRequest(requestParams.request);
        let body = JSON.stringify(requestParams.request);

        const requestBuilder = opts?.requestBuilder;
        if (requestBuilder) {
          body = JSON.stringify(requestBuilder(requestParams));
        }

        const headers = opts?.headers || new HttpHeaders({});
        return firstValueFrom(this.http.post<T>(serverUrl, body, { headers }))
          .then((response) => {
            responseCallBack(response, requestParams);
          })
          .catch((error) => {
            const responseErrorCallback = opts?.responseErrorCallback;
            if (responseErrorCallback) {
              responseErrorCallback(error, requestParams);
            } else {
              console.error(error);
              requestParams.fail();
            }
          });
      },
    };
  }

  private formatDateString(request: IServerSideGetRowsParams['request'], index: number) {
    const groupKey = request.groupKeys[index];
    if (groupKey && groupKey !== 'NULL') {
      request.groupKeys[index] = groupKey.replaceAll(/[ -:]/g, '');
    }
  }
}
